import {DatePicker} from '@github-ui/date-picker'
import {FormControl} from '@primer/react'
import {clsx} from 'clsx'

import type {IterationDuration} from '../../../helpers/iterations'
import {Resources} from '../../../strings'
import {IterationDurationInput, type ValidationProps} from './iteration-duration-input'
import styles from './new-iteration-options.module.css'

interface NewIterationOptionsProps {
  /** Selected duration. */
  duration: IterationDuration
  /** Called when duration changed. */
  onDurationChange: (duration: IterationDuration) => void
  /** Selected start date. */
  startDate: Date
  /** Called on start date change. Start date cannot be cleared so it's always defined. */
  onStartDateChange: (startDate: Date) => void
  /** Minimum allowed start date. The start date can equal this but not be before it. */
  minStartDate?: Date
  /** When true removes the default width:100% and align-items: initial styles */
  compactLayout?: boolean
  /** Optional validation configuration for the input. */
  validation?: ValidationProps
}

/**
 * Stateless user input for the options required to create a new iteration field / instance.
 */
export function NewIterationOptions({
  duration,
  onDurationChange,
  startDate,
  onStartDateChange,
  minStartDate,
  compactLayout = false,
  validation,
}: NewIterationOptionsProps) {
  const onDatePickerChange = (date?: Date | null) => {
    // This should not be possible since the date cannot be cleared
    if (!date) throw new Error('Start date cannot be null/undefined.')
    onStartDateChange(date)
  }

  return (
    <div className={clsx(styles.NewIterationOptions, compactLayout && styles.Compact)}>
      <FormControl id="iteration-start-date" className={styles.StartDateFormControl}>
        <FormControl.Label>{Resources.iterationStartDateLabel}</FormControl.Label>

        <DatePicker
          aria-label={Resources.iterationStartDateLabel}
          variant="single"
          value={startDate}
          onChange={onDatePickerChange}
          minDate={minStartDate}
          showTodayButton={false}
          anchorClassName={styles.DatePicker}
        />
      </FormControl>

      <FormControl id="duration-quantity-input" className={styles.DurationFormControl}>
        <FormControl.Label>{Resources.iterationDurationLabel}</FormControl.Label>
        <IterationDurationInput
          value={duration}
          onChange={onDurationChange}
          compactLayout={compactLayout}
          validation={validation}
        />

        {validation?.showValidationErrors !== false &&
        validation?.validationStyle === 'inline' &&
        validation?.validationMessage ? (
          <FormControl.Validation variant="error">{validation?.validationMessage}</FormControl.Validation>
        ) : null}
      </FormControl>
    </div>
  )
}
